/**
 * @description Firebase Firestore functions for weight logging
 * Add these functions to your existing firebase service file
 */

import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  deleteDoc,
  doc,
  serverTimestamp,
  orderBy,
  Timestamp,
} from "firebase/firestore";
import { db } from "./firebase";

/**
 * Add a weight log entry
 * @param {string} uid – User ID
 * @param {Object} data – { weight, date, notes }
 * @returns {Promise<string>} – Document ID
 */
export async function addWeightLog(uid, { weight, date, notes = "" }) {
  try {
    const docRef = await addDoc(collection(db, "users", uid, "weightLogs"), {
      weight: parseFloat(weight),
      date: date instanceof Date ? date : new Date(date),
      notes: notes.trim(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return docRef.id;
  } catch (error) {
    console.error("Error adding weight log:", error);
    throw error;
  }
}

/**
 * Get all weight logs for a user
 * @param {string} uid – User ID
 * @returns {Promise<Array>} – Weight log documents with IDs
 */
export async function getWeightLogs(uid) {
  try {
    const q = query(
      collection(db, "users", uid, "weightLogs"),
      orderBy("date", "desc"),
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      // Normalize date to simple string or Date object
      date: doc.data().date?.toDate?.() || new Date(doc.data().date),
    }));
  } catch (error) {
    console.error("Error fetching weight logs:", error);
    return [];
  }
}

/**
 * Delete a weight log entry
 * @param {string} uid – User ID
 * @param {string} logId – Weight log document ID
 * @returns {Promise<void>}
 */
export async function deleteWeightLog(uid, logId) {
  try {
    await deleteDoc(doc(db, "users", uid, "weightLogs", logId));
  } catch (error) {
    console.error("Error deleting weight log:", error);
    throw error;
  }
}

/**
 * Get weight logs for a specific date range
 * @param {string} uid – User ID
 * @param {Date} startDate – Start date
 * @param {Date} endDate – End date
 * @returns {Promise<Array>} – Filtered weight logs
 */
export async function getWeightLogsByDateRange(uid, startDate, endDate) {
  try {
    const q = query(
      collection(db, "users", uid, "weightLogs"),
      where("date", ">=", startDate),
      where("date", "<=", endDate),
      orderBy("date", "asc"),
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      date: doc.data().date?.toDate?.() || new Date(doc.data().date),
    }));
  } catch (error) {
    console.error("Error fetching weight logs by date range:", error);
    return [];
  }
}

export async function getWorkoutStreaks(uid, currentMonth) {
  const startOfMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth(),
    1,
  );
  const endOfMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth() + 1, // Move to next month
    0, // Day 0 is the last day of the previous month
    23,
    59,
    59,
    999,
  );

  try {
    const ref = collection(db, "users", uid, "workouts");
    const q = query(
      ref,
      where("date", ">=", startOfMonth),
      where("date", "<=", endOfMonth),
    );

    const snap = await getDocs(q);
    const days = new Set(
      snap.docs.map((doc) => {
        const date = doc.data().date.toDate();
        return date.getDate();
      }),
    );
    return days;
  } catch (error) {
    console.error("❌ Error fetching workout streaks:", error);
    throw new Error(error?.message || "Failed to fetch workout streaks");
  }
}

/**
 * Toggles a workout for a specific date (Add if missing, Delete if exists)
 * @param {string} uid - User ID
 * @param {number} day - Day of the month
 * @param {Date} currentMonth - Date object representing the month/year
 * @param {boolean} isDelete - Whether we are removing or adding
 */
export async function toggleWorkoutDay(uid, day, currentMonth, isDelete) {
  try {
    const date = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day,
    );
    date.setHours(0, 0, 0, 0);
    const firestoreTimestamp = Timestamp.fromDate(date);
    const colRef = collection(db, "users", uid, "workouts");

    if (isDelete) {
      // Find the specific document for this date to delete it
      const q = query(colRef, where("date", "==", firestoreTimestamp));
      const snap = await getDocs(q);

      const deletePromises = snap.docs.map((doc) => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
      return { action: "deleted", day };
    } else {
      // Add a new workout document
      const docRef = await addDoc(colRef, {
        date: firestoreTimestamp,
        type: "gym", // Default type
        createdAt: Timestamp.now(),
      });
      return { action: "added", id: docRef.id, day };
    }
  } catch (error) {
    console.error("❌ Error toggling workout:", error);
    throw error;
  }
}
